import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ExternalLink, Save, Video, Loader2, Plus, Trash2 } from "lucide-react";

const fileUrl = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
const inp = "w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none";
const ta = inp + " leading-relaxed";

export default function ProfileSiteEditor({ companyId, canManage }) {
  const [site, setSite] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get(`/public/company-profile/${companyId}`).then(({ data }) => setSite(data.site)).catch(() => {});
  useEffect(() => { if (companyId) load(); }, [companyId]);

  if (!companyId) return null;
  const set = (k, v) => setSite((s) => ({ ...s, [k]: v }));
  const openPublic = () => window.open(`/p/${site?.slug || companyId}`, "_blank");

  const save = async () => {
    setSaving(true);
    try { await api.put(`/companies/${companyId}/profile-site`, site); toast.success("Profile page saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  const uploadVideo = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      set("video_url", data.url); toast.success("Video uploaded — remember to save");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); e.target.value = ""; }
  };

  const uploadImage = async (file, cb) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      cb(data.url);
      toast.success("Image uploaded — remember to save");
    } catch { toast.error("Upload failed"); }
  };
  const gUrl = (g) => (typeof g === "string" ? g : g?.url || "");
  const gCap = (g) => (typeof g === "string" ? "" : g?.caption || "");
  const galleryAdd = (u) => set("aim_project", { ...site.aim_project, gallery: [...(site.aim_project?.gallery || []), { url: u, caption: "" }] });
  const galleryDel = (i) => set("aim_project", { ...site.aim_project, gallery: (site.aim_project?.gallery || []).filter((_, x) => x !== i) });
  const galleryCap = (i, caption) => set("aim_project", { ...site.aim_project, gallery: (site.aim_project?.gallery || []).map((g, x) => (x === i ? { url: gUrl(g), caption } : g)) });

  if (!site)
    return <div className="bg-white rounded-2xl border border-slate-200 p-6 text-slate-400 text-sm">Loading profile page…</div>;

  const listSet = (key, i, field, val) => { const arr = [...(site[key] || [])]; arr[i] = { ...arr[i], [field]: val }; set(key, arr); };
  const listAdd = (key, obj) => set(key, [...(site[key] || []), obj]);
  const listDel = (key, i) => set(key, (site[key] || []).filter((_, idx) => idx !== i));

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6" data-testid="profile-site-editor">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-indigo-600" />
          <h3 className="font-heading font-bold text-slate-800">Public Profile Page</h3>
        </div>
        <div className="flex gap-2">
          <button data-testid="open-public-profile" onClick={openPublic} className="px-3 py-2 rounded-xl border border-slate-200 font-semibold text-sm flex items-center gap-2 hover:bg-slate-50"><ExternalLink size={15} /> Open Profile</button>
          {canManage && <button data-testid="save-profile-site" onClick={save} disabled={saving} className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm flex items-center gap-2">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</button>}
        </div>
      </div>

      <fieldset disabled={!canManage} className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <Row label="Theme">
            <div className="flex gap-2">
              {["emerald", "indigo", "amber"].map((th) => (
                <button key={th} type="button" data-testid={`theme-${th}`} onClick={() => set("theme", th)}
                  className={`px-3 py-2 rounded-xl border text-sm font-semibold capitalize ${(site.theme || "emerald") === th ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600"}`}>
                  <span className={`inline-block w-3 h-3 rounded-full mr-1.5 align-middle ${th === "emerald" ? "bg-emerald-500" : th === "indigo" ? "bg-indigo-500" : "bg-amber-500"}`} />{th}
                </button>
              ))}
            </div>
          </Row>
          <Row label="Custom link">
            <div className="flex items-center gap-1">
              <span className="text-sm text-slate-400 whitespace-nowrap">/p/</span>
              <input className={inp} data-testid="ps-slug" value={site.slug || ""} placeholder="greenvolt"
                onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} />
            </div>
          </Row>
        </div>
        <Row label="Hero tagline"><input className={inp} value={site.hero_tagline || ""} onChange={(e) => set("hero_tagline", e.target.value)} data-testid="ps-tagline" /></Row>
        <Row label="Introduction"><textarea rows={3} className={ta} value={site.intro || ""} onChange={(e) => set("intro", e.target.value)} /></Row>
        <div className="grid md:grid-cols-2 gap-4">
          <Row label="Mission"><textarea rows={3} className={ta} value={site.mission || ""} onChange={(e) => set("mission", e.target.value)} /></Row>
          <Row label="Vision"><textarea rows={3} className={ta} value={site.vision || ""} onChange={(e) => set("vision", e.target.value)} /></Row>
        </div>
        <Row label="Company profile"><textarea rows={3} className={ta} value={site.company_profile || ""} onChange={(e) => set("company_profile", e.target.value)} /></Row>

        {/* Video */}
        <Row label="Company / CEO video">
          <div className="flex items-center gap-3">
            {site.video_url && <video src={fileUrl(site.video_url)} className="w-40 rounded-lg border border-slate-200" controls />}
            <label className="px-4 py-2.5 rounded-xl border-2 border-dashed border-slate-200 cursor-pointer hover:border-indigo-400 text-sm font-medium text-slate-600 flex items-center gap-2">
              <input type="file" accept="video/*" className="hidden" data-testid="ps-video" onChange={uploadVideo} />
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Video size={16} />}{site.video_url ? "Replace video" : "Upload video"}
            </label>
          </div>
        </Row>

        {/* Expertise */}
        <ListEditor label="Key areas of expertise" items={site.expertise || []} onAdd={() => listAdd("expertise", { title: "", desc: "" })} onDel={(i) => listDel("expertise", i)}
          render={(it, i) => (<>
            <input className={inp} placeholder="Title" value={it.title || ""} onChange={(e) => listSet("expertise", i, "title", e.target.value)} />
            <input className={inp} placeholder="Description" value={it.desc || ""} onChange={(e) => listSet("expertise", i, "desc", e.target.value)} />
          </>)} />

        {/* Aim project */}
        <div className="rounded-xl bg-emerald-50/60 border border-emerald-100 p-4 space-y-3">
          <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">Aim Project</p>
          <input className={inp} value={site.aim_project?.title || ""} onChange={(e) => set("aim_project", { ...site.aim_project, title: e.target.value })} placeholder="Title" />
          <textarea rows={3} className={ta} value={site.aim_project?.description || ""} onChange={(e) => set("aim_project", { ...site.aim_project, description: e.target.value })} placeholder="Description" />
          <textarea rows={2} className={ta} value={(site.aim_project?.highlights || []).join("\n")} onChange={(e) => set("aim_project", { ...site.aim_project, highlights: e.target.value.split("\n").filter(Boolean) })} placeholder="One highlight per line" />
          <div>
            <p className="text-xs font-semibold text-emerald-700 mb-1">Project gallery (solar-greenhouse photos)</p>
            <div className="flex flex-wrap gap-3">
              {(site.aim_project?.gallery || []).map((g, i) => (
                <div key={i} className="relative w-28">
                  <img src={fileUrl(gUrl(g))} alt="" className="w-28 h-20 rounded-lg object-cover border border-slate-200" />
                  <button type="button" onClick={() => galleryDel(i)} className="absolute -top-1.5 -right-1.5 bg-white rounded-full border border-slate-200 p-0.5 shadow-sm"><Trash2 size={11} className="text-rose-500" /></button>
                  <input className={inp + " mt-1.5 px-2 py-1 text-xs"} maxLength={140} data-testid={`gallery-caption-${i}`}
                    placeholder="Add a caption…" value={gCap(g)} onChange={(e) => galleryCap(i, e.target.value)} />
                </div>
              ))}
              <label className="w-28 h-20 rounded-lg border-2 border-dashed border-slate-300 grid place-items-center cursor-pointer hover:border-indigo-400" data-testid="gallery-add">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files[0], galleryAdd)} />
                <Plus size={16} className="text-slate-400" />
              </label>
            </div>
          </div>
        </div>

        {/* Board of directors */}
        <ListEditor label="Board of directors" items={site.directors || []} onAdd={() => listAdd("directors", { name: "", role: "", bio: "" })} onDel={(i) => listDel("directors", i)}
          render={(it, i) => (<>
            <input className={inp} placeholder="Name" value={it.name || ""} onChange={(e) => listSet("directors", i, "name", e.target.value)} />
            <input className={inp} placeholder="Role / designation" value={it.role || ""} onChange={(e) => listSet("directors", i, "role", e.target.value)} />
            <input className={inp} placeholder="Short bio" value={it.bio || ""} onChange={(e) => listSet("directors", i, "bio", e.target.value)} />
            <div className="flex items-center gap-2">
              {it.photo && <img src={fileUrl(it.photo)} alt="" className="w-9 h-9 rounded-full object-cover border border-slate-200" />}
              <label className="text-xs font-semibold text-indigo-600 cursor-pointer flex items-center gap-1">
                <input type="file" accept="image/*" className="hidden" data-testid={`director-photo-${i}`} onChange={(e) => uploadImage(e.target.files[0], (u) => listSet("directors", i, "photo", u))} />
                {it.photo ? "Change headshot" : "Add headshot"}
              </label>
            </div>
          </>)} />

        {/* Contact */}
        <div className="grid md:grid-cols-3 gap-3">
          <Row label="Contact email"><input className={inp} value={site.contact?.email || ""} onChange={(e) => set("contact", { ...site.contact, email: e.target.value })} data-testid="ps-contact-email" /></Row>
          <Row label="Contact phone"><input className={inp} value={site.contact?.phone || ""} onChange={(e) => set("contact", { ...site.contact, phone: e.target.value })} /></Row>
          <Row label="Contact address"><input className={inp} value={site.contact?.address || ""} onChange={(e) => set("contact", { ...site.contact, address: e.target.value })} /></Row>
        </div>
      </fieldset>
    </div>
  );
}

const Row = ({ label, children }) => (<div><label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>{children}</div>);

function ListEditor({ label, items, render, onAdd, onDel }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-semibold text-slate-600">{label}</label>
        <button type="button" onClick={onAdd} className="text-xs font-semibold text-indigo-600 flex items-center gap-1"><Plus size={13} /> Add</button>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-start gap-2 p-2 rounded-xl border border-slate-100 bg-slate-50/60">
            <div className="flex-1 grid gap-2">{render(it, i)}</div>
            <button type="button" onClick={() => onDel(i)} className="p-1.5 text-slate-400 hover:text-rose-600"><Trash2 size={15} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-slate-400">None yet — click Add.</p>}
      </div>
    </div>
  );
}
